import React from 'react'
import { createBrowserRouter, RouteObject, redirect } from 'react-router-dom'
import {
  requireSessionLoader,
  undoRouteLoader,
  chatbotGuardLoader,
  stepJumpLoader,
  faqGuardLoader,
  marketplaceGuardLoader,
  portalAuthedGuardLoader,
  portalLoginGuardLoader,
} from './guards'

import Layout from '@/components/Layout'
import OmpLayout from '@/components/OrderManagementPortal/OmpLayout'
import OmpLogin from '@/components/OrderManagementPortal/pages/OmpLogin'

const OmpHome = React.lazy(() => import('@/components/OrderManagementPortal/pages/OmpHome'))
const OmpSubscriptions = React.lazy(() => import('@/components/OrderManagementPortal/pages/OmpSubscriptions'))
const OmpSubscriptionDetails = React.lazy(
  () => import('@/components/OrderManagementPortal/pages/OmpSubscriptionDetails')
)
const OmpOrders = React.lazy(() => import('@/components/OrderManagementPortal/pages/OmpOrders'))
const OmpOrderDetails = React.lazy(() => import('@/components/OrderManagementPortal/pages/OmpOrderDetails'))
const OmpAccount = React.lazy(() => import('@/components/OrderManagementPortal/pages/OmpAccount'))
const OmpAddresses = React.lazy(() => import('@/components/OrderManagementPortal/pages/OmpAddresses'))
const OmpPaymentMethods = React.lazy(() => import('@/components/OrderManagementPortal/pages/OmpPaymentMethods'))

const SessionMainPage = React.lazy(() => import('@/pages/SessionMainPage'))
const MatchPage = React.lazy(() => import('@/pages/MatchPage'))
const SessionJumpPage = React.lazy(() => import('@/pages/SessionJumpPage'))
const UndoPage = React.lazy(() => import('@/pages/UndoPage'))
const ChatbotPage = React.lazy(() => import('@/pages/ChatbotPage'))
const ChatbotEmbedPage = React.lazy(() => import('@/components/ChatbotEmbed'))
const AuthTokenPage = React.lazy(() => import('@/pages/AuthTokenPage'))
const LogoutPage = React.lazy(() => import('@/pages/LogoutPage'))
const ProductsPage = React.lazy(() => import('@/components/Products'))
const ProductDetailsPage = React.lazy(() => import('@/components/ProductDetails'))

// FAQ components
const FaqTopics = React.lazy(() => import('@/components/Faq/FaqTopics'))
const Faqs = React.lazy(() => import('@/components/Faq/Faqs'))
const FaqDetails = React.lazy(() => import('@/components/Faq/FaqDetails'))
const FaqSearch = React.lazy(() => import('@/components/Faq/FaqSearch'))

const NotFoundPage = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="text-center max-w-md mx-auto p-8">
      <h1 className="text-6xl font-bold text-gray-200 mb-4">404</h1>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h2>
      <p className="text-gray-600 mb-8">The page you're looking for doesn't exist or may have been moved.</p>
      <a
        href="/"
        className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium">
        ← Go Home
      </a>
    </div>
  </div>
)

const RouterErrorBoundary = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="text-center max-w-md mx-auto p-8">
      <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
      <p className="text-gray-600 mb-8">We encountered an error while loading this page. Please try again.</p>
      <button
        onClick={() => window.location.reload()}
        className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium">
        Reload Page
      </button>
    </div>
  </div>
)

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      // Main session page — also handles session initialization
      {
        index: true,
        element: <SessionMainPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // Customer match / login
      {
        path: 'match',
        element: <MatchPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // /session/start — redirect to / (session start)
      {
        path: 'session/start',
        loader: ({ request }) => {
          const url = new URL(request.url)
          const dest = new URL('/', url.origin)
          url.searchParams.forEach((v, k) => dest.searchParams.set(k, v))
          throw redirect(dest.toString())
        },
        element: null,
      },
      {
        path: 'session/start/:step',
        loader: ({ params }) => {
          throw redirect(`/start/${params.step}`)
        },
        element: null,
      },
      // /session and /session/:step share the same route so navigating between
      // them does NOT remount SessionMainPage (which would refetch /order_items/).
      {
        path: 'session/*',
        element: <SessionMainPage />,
        loader: requireSessionLoader,
        errorElement: <RouterErrorBoundary />,
      },
      // Deep-link jump to a specific step — requires valid session + can_jump
      // If no session, redirect to /start/:step to start a new session and jump
      {
        path: 'step/:step',
        element: <SessionJumpPage />,
        loader: stepJumpLoader,
        errorElement: <RouterErrorBoundary />,
      },
      // Start a new session and jump to a specific step
      {
        path: 'start/:step',
        element: <SessionMainPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // Undo the last action
      {
        path: 'undo',
        element: <UndoPage />,
        loader: undoRouteLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'undo/:step',
        element: <UndoPage />,
        loader: undoRouteLoader,
        errorElement: <RouterErrorBoundary />,
      },
      // AuthToken routes
      {
        path: 'auth/:uid/:token',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'auth/:uid/:token/:step',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'auth/:uid/:token/start/:start',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // Products routes — gated by show_marketplace
      {
        path: 'products',
        element: <ProductsPage />,
        loader: marketplaceGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'products/:id/:slug',
        element: <ProductDetailsPage />,
        loader: marketplaceGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'chat',
        element: <ChatbotPage />,
        loader: chatbotGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'chatbot_embed',
        element: <ChatbotEmbedPage />,
        loader: chatbotGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ':uidToken',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ':uidToken/start/:start',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ':uidToken/step/:step',
        element: <AuthTokenPage />,
        errorElement: <RouterErrorBoundary />,
      },
      // FAQ routes — gated by show_faqs
      {
        path: 'topics',
        element: <FaqTopics />,
        loader: faqGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'faqs',
        element: <Faqs />,
        loader: faqGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'faqs/search',
        element: <FaqSearch />,
        loader: faqGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'faqs/:slug',
        element: <FaqDetails />,
        loader: faqGuardLoader,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: 'logout',
        element: <LogoutPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
  // Order Management Portal — dedicated shell + routes, entered when the
  // session's campaign has subdomain_type === CustomerPortal (4).
  // Login (customer match) lives outside the authed shell.
  {
    path: '/portal/login',
    element: <OmpLogin />,
    loader: portalLoginGuardLoader,
    errorElement: <RouterErrorBoundary />,
  },
  {
    path: '/portal',
    element: <OmpLayout />,
    loader: portalAuthedGuardLoader,
    errorElement: <RouterErrorBoundary />,
    children: [
      { index: true, element: <OmpHome /> },
      { path: 'subscriptions', element: <OmpSubscriptions /> },
      { path: 'subscriptions/:id', element: <OmpSubscriptionDetails /> },
      { path: 'orders', element: <OmpOrders /> },
      { path: 'orders/:id', element: <OmpOrderDetails /> },
      { path: 'account', element: <OmpAccount /> },
      { path: 'account/addresses', element: <OmpAddresses /> },
      { path: 'account/payment', element: <OmpPaymentMethods /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]

export const router = createBrowserRouter(routes)
export default router
